import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  listScenarios, createScenario, deleteScenario, refreshScenario,
  addScenarioFireAction, removeScenarioFireAction, getTeamMembers, getTeamRoles,
  createScenarioProject, deleteScenarioProject,
} from '../api/client'
import PageHeader from '../components/PageHeader'
import Modal from '../components/Modal'
import ScenarioProjectPanel from '../components/staffing/ScenarioProjectPanel'

/**
 * Staffing scenarios: a c_level-only "what if I fire someone" sandbox.
 * Every scenario is a full, self-contained clone of the company's real
 * staffing (see backend/scenario_staffing.py) -- creating/editing one never
 * touches the real Dotacion, and the clone only updates when "Actualizar" is
 * pressed. Selecting a scenario here doesn't change anything by itself; the
 * scenario selector on the Staffing (Disponibilidad) tab is what actually
 * switches that view to a scenario's numbers.
 */
export default function Scenarios() {
  const { t } = useTranslation(['clevel', 'common'])
  const [scenarios, setScenarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [addingAction, setAddingAction] = useState(false)
  const [addingProject, setAddingProject] = useState(false)
  const [openProjectId, setOpenProjectId] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = async (keepSelection = true) => {
    setLoading(true)
    try {
      const data = await listScenarios()
      setScenarios(data)
      if (!keepSelection || !data.some(s => s.id === selectedId)) {
        setSelectedId(data[0]?.id ?? null)
      }
    } catch (_) {}
    setLoading(false)
  }
  useEffect(() => { load(false) }, [])

  const selected = scenarios.find(s => s.id === selectedId) || null

  const handleDelete = async (scenario) => {
    if (!confirm(t('scenarios.deleteConfirm', { name: scenario.name }))) return
    setBusy(true)
    try {
      await deleteScenario(scenario.id)
      await load(false)
    } catch (e) {
      alert(e.response?.data?.detail || t('scenarios.deleteFailed'))
    }
    setBusy(false)
  }

  const handleRefresh = async (scenario) => {
    setBusy(true)
    try {
      await refreshScenario(scenario.id)
      await load()
    } catch (e) {
      alert(e.response?.data?.detail || t('scenarios.refreshFailed'))
    }
    setBusy(false)
  }

  const handleRemoveAction = async (scenario, action) => {
    if (!confirm(t('scenarios.removeActionConfirm'))) return
    setBusy(true)
    try {
      await removeScenarioFireAction(scenario.id, action.id)
      await load()
    } catch (e) {
      alert(e.response?.data?.detail || t('scenarios.removeActionFailed'))
    }
    setBusy(false)
  }

  const handleDeleteProject = async (scenario, project) => {
    if (!confirm(t('scenarios.deleteProjectConfirm', { name: project.name }))) return
    setBusy(true)
    try {
      await deleteScenarioProject(scenario.id, project.id)
      await load()
    } catch (e) {
      alert(e.response?.data?.detail || t('scenarios.deleteProjectFailed'))
    }
    setBusy(false)
  }

  return (
    <div className="p-8">
      <PageHeader
        title={t('scenarios.title')}
        subtitle={t('scenarios.subtitle')}
        actions={
          <button
            onClick={() => setCreating(true)}
            className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
          >
            {t('scenarios.newButton')}
          </button>
        }
      />

      <p className="text-xs text-gray-400 my-4 max-w-2xl">{t('scenarios.intro')}</p>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden h-fit">
          {loading ? (
            <p className="text-sm text-gray-400 px-5 py-6">{t('scenarios.loading')}</p>
          ) : scenarios.length === 0 ? (
            <p className="text-sm text-gray-400 px-5 py-6">{t('scenarios.empty')}</p>
          ) : (
            <ul>
              {scenarios.map(s => (
                <li key={s.id}>
                  <button
                    onClick={() => setSelectedId(s.id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-b-0 transition-colors ${
                      selectedId === s.id ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                  >
                    <div className="text-sm font-medium text-gray-900">{s.name}</div>
                    <div className="text-xs text-gray-400">
                      {t('scenarios.fireActionCount', { count: s.fire_actions.length })}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selected && (
          <ScenarioDetail
            t={t}
            scenario={selected}
            busy={busy}
            onDelete={() => handleDelete(selected)}
            onRefresh={() => handleRefresh(selected)}
            onAddAction={() => setAddingAction(true)}
            onRemoveAction={(action) => handleRemoveAction(selected, action)}
            onAddProject={() => setAddingProject(true)}
            onOpenProject={(project) => setOpenProjectId(project.id)}
            onDeleteProject={(project) => handleDeleteProject(selected, project)}
          />
        )}
      </div>

      {creating && (
        <CreateScenarioModal
          t={t}
          onClose={() => setCreating(false)}
          onCreated={async (created) => {
            setCreating(false)
            await load(false)
            setSelectedId(created.id)
          }}
        />
      )}

      {openProjectId && selected && selected.projects.some(p => p.id === openProjectId) && (
        <ScenarioProjectPanel
          key={openProjectId}
          scenario={selected}
          project={selected.projects.find(p => p.id === openProjectId)}
          onClose={() => setOpenProjectId(null)}
          onChanged={() => load()}
        />
      )}

      {addingProject && selected && (
        <AddProjectModal
          t={t}
          scenario={selected}
          onClose={() => setAddingProject(false)}
          onAdded={async () => { setAddingProject(false); await load() }}
        />
      )}

      {addingAction && selected && (
        <AddFireActionModal
          t={t}
          scenario={selected}
          onClose={() => setAddingAction(false)}
          onAdded={async () => { setAddingAction(false); await load() }}
        />
      )}
    </div>
  )
}

function ScenarioDetail({ t, scenario, busy, onDelete, onRefresh, onAddAction, onRemoveAction, onAddProject, onDeleteProject, onOpenProject }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{scenario.name}</h2>
          {scenario.notes && <p className="text-sm text-gray-500 mt-0.5">{scenario.notes}</p>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onRefresh}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {t('scenarios.update')}
          </button>
          <button
            onClick={onDelete}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            {t('common:actions.delete')}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        {scenario.refreshed_at
          ? t('scenarios.lastUpdated', { date: new Date(scenario.refreshed_at).toLocaleString() })
          : t('scenarios.neverUpdated')}
      </p>

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">{t('scenarios.fireActions')}</h3>
        <button
          onClick={onAddAction}
          className="text-xs px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700"
        >
          {t('scenarios.addFireAction')}
        </button>
      </div>

      {scenario.fire_actions.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">{t('scenarios.noFireActions')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 text-xs font-semibold text-gray-500">{t('scenarios.columns.person')}</th>
              <th className="text-left py-2 text-xs font-semibold text-gray-500">{t('scenarios.columns.fireDate')}</th>
              <th className="text-left py-2 text-xs font-semibold text-gray-500">{t('scenarios.columns.replacementRole')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {scenario.fire_actions.map(a => (
              <tr key={a.id} className="border-b border-gray-50 last:border-b-0">
                <td className="py-2 text-gray-900">{a.team_member_name || `#${a.team_member_id}`}</td>
                <td className="py-2 text-gray-500">{a.fire_date}</td>
                <td className="py-2 text-gray-500">{a.replacement_role}</td>
                <td className="py-2 text-right">
                  <button onClick={() => onRemoveAction(a)} className="text-xs text-gray-400 hover:text-red-500">
                    {t('common:actions.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex items-center justify-between mt-8 mb-2">
        <h3 className="text-sm font-semibold text-gray-900">{t('scenarios.projects')}</h3>
        <button
          onClick={onAddProject}
          className="text-xs px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700"
        >
          {t('scenarios.addProject')}
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-2">{t('scenarios.projectsHint')}</p>
      {scenario.projects.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">{t('scenarios.noProjects')}</p>
      ) : (
        <ul>
          {scenario.projects.map(p => (
            <li key={p.id} onClick={() => onOpenProject(p)} title={t('scenarios.openProject')}
              className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0 text-sm">
              <span className="text-amber-700" title={t('scenarios.badgeHint')}>◆</span>
              <span className="text-gray-900">{p.code ? `${p.code} · ${p.name}` : p.name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                {p.propio ? t('scenarios.projectOwn') : t('scenarios.projectCopy')}
              </span>
              {p.propio && (
                <button onClick={(e) => { e.stopPropagation(); onDeleteProject(p) }} className="ml-auto text-xs text-gray-400 hover:text-red-500">
                  {t('common:actions.delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AddProjectModal({ t, scenario, onClose, onAdded }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!name.trim()) { setError(t('scenarios.createModal.nameRequired')); return }
    setBusy(true)
    setError('')
    try {
      await createScenarioProject(scenario.id, { name: name.trim(), code: code.trim() || null })
      onAdded()
    } catch (e) {
      setError(e.response?.data?.detail || t('scenarios.addProjectFailed'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('scenarios.addProject')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(420px, 80vw)' }}>
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <p className="text-xs text-gray-400">{t('scenarios.projectsHint')}</p>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('scenarios.createModal.name')}</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit() }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('scenarios.projectCode')}</label>
          <input
            value={code}
            onChange={e => setCode(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={submit}
            disabled={busy || !name.trim()}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
          >
            {t('scenarios.createModal.create')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function CreateScenarioModal({ t, onClose, onCreated }) {
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!name.trim()) { setError(t('scenarios.createModal.nameRequired')); return }
    setBusy(true)
    setError('')
    try {
      const created = await createScenario(name.trim(), notes.trim() || null)
      onCreated(created)
    } catch (e) {
      setError(e.response?.data?.detail || t('scenarios.createModal.createFailed'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('scenarios.createModal.title')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(420px, 80vw)' }}>
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('scenarios.createModal.name')}</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit() }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('scenarios.createModal.notes')}</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={submit}
            disabled={busy || !name.trim()}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
          >
            {busy ? t('scenarios.createModal.creating') : t('scenarios.createModal.create')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function AddFireActionModal({ t, scenario, onClose, onAdded }) {
  const [members, setMembers] = useState([])
  const [roles, setRoles] = useState([])
  const [teamMemberId, setTeamMemberId] = useState('')
  const [fireDate, setFireDate] = useState('')
  const [replacementRole, setReplacementRole] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    getTeamMembers({ plannable: true }).then(setMembers).catch(() => {})
    getTeamRoles().then(setRoles).catch(() => {})
  }, [])

  const onMemberChange = (id) => {
    setTeamMemberId(id)
    const member = members.find(m => String(m.id) === String(id))
    // Defaults to the fired person's own current Cargo -- still editable.
    if (member?.role) setReplacementRole(member.role)
  }

  const submit = async () => {
    if (!teamMemberId || !fireDate || !replacementRole) {
      setError(t('scenarios.addActionModal.allFieldsRequired'))
      return
    }
    setBusy(true)
    setError('')
    try {
      await addScenarioFireAction(scenario.id, {
        teamMemberId: Number(teamMemberId), fireDate, replacementRole,
      })
      onAdded()
    } catch (e) {
      setError(e.response?.data?.detail || t('scenarios.addActionModal.addFailed'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('scenarios.addActionModal.title')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(420px, 80vw)' }}>
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('scenarios.addActionModal.person')}</label>
          <select
            value={teamMemberId}
            onChange={e => onMemberChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">{t('scenarios.addActionModal.selectPerson')}</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('scenarios.addActionModal.fireDate')}</label>
          <input
            type="date"
            value={fireDate}
            onChange={e => setFireDate(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('scenarios.addActionModal.replacementRole')}
          </label>
          <select
            value={replacementRole}
            onChange={e => setReplacementRole(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">{t('scenarios.addActionModal.selectRole')}</option>
            {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
        </div>
        <div className="flex gap-3">
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
          >
            {busy ? t('scenarios.addActionModal.adding') : t('scenarios.addActionModal.add')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
